import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card, PageHeader, Btn, Input, Select, Textarea } from "../components/UI";
import { ArrowRight, ArrowLeft, ShieldCheck, Check, Vote } from "lucide-react";

const ENTITY_TYPES = [
  { value: "candidate", label: "Candidate" },
  { value: "candidate_committee", label: "Candidate Committee" },
  { value: "pac", label: "PAC" },
  { value: "super_pac", label: "Super PAC" },
  { value: "party", label: "Political Party" },
  { value: "ballot_measure", label: "Ballot Measure" },
  { value: "iec", label: "Independent Expenditure Committee" },
  { value: "issue_advocacy", label: "Issue Advocacy Organization" },
  { value: "public_affairs", label: "Public Affairs Organization" },
  { value: "government", label: "Government / Public Info" },
  { value: "nonprofit_advocacy", label: "Nonprofit Advocacy" },
  { value: "other", label: "Other Eligible Political Organization" },
];

const AUTH_OPTS = [
  "authorized_candidate_committee",
  "authorized_by_candidate",
  "not_authorized_by_candidate",
  "independent_expenditure",
  "pac",
  "super_pac",
  "ballot_committee",
  "issue_advocacy",
  "public_information",
];

const STEPS = ["Who are you?", "Campaign details", "Compliance & authorization", "Branding", "Review"];

export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    entity_type: "candidate_committee",
    legal_org_name: "",
    public_campaign_name: "",
    candidate_name: "",
    office_sought: "",
    election: "2026 General",
    election_date: "",
    jurisdiction: "",
    state: "",
    district: "",
    party: "",
    committee_name: "",
    fec_id: "",
    state_registration_id: "",
    treasurer: "",
    compliance_contact: "",
    legal_counsel: "",
    website: "",
    social_handles: {},
    brand_primary_color: "#fbbf24",
    brand_secondary_color: "#d97706",
    approved_messaging: "",
    approved_disclaimers: [""],
    authorization_status: "authorized_candidate_committee",
    monthly_budget: 0,
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        monthly_budget: parseFloat(form.monthly_budget) || 0,
        approved_disclaimers: form.approved_disclaimers.filter(Boolean),
      };
      await api.post("/political-entities", payload);
      navigate("/dashboard");
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const next = () => setStep(s => Math.min(STEPS.length - 1, s + 1));
  const back = () => setStep(s => Math.max(0, s - 1));

  return (
    <div className="min-h-screen bg-app flex items-center justify-center p-6">
      <div className="w-full max-w-3xl">
        <div className="mb-6 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center">
            <Vote className="w-5 h-5 text-slate-900" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-cyan-300 font-medium">Political Entity Setup</div>
            <div className="font-display text-2xl font-bold">Tell us who you're advertising or fundraising for.</div>
          </div>
        </div>

        {/* Stepper */}
        <div className="flex mb-4">
          {STEPS.map((s, i) => (
            <div key={s} className="flex-1 flex items-center">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                i < step ? "bg-cyan-400 text-slate-900" :
                i === step ? "bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900" :
                "bg-white/5 text-slate-500 border border-white/10"
              }`}>{i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}</div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 ${i < step ? "bg-cyan-400" : "bg-white/10"}`} />}
            </div>
          ))}
        </div>
        <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-4">{STEPS[step]}</div>

        <Card>
          {step === 0 && (
            <div className="space-y-4" data-testid="onboarding-step-0">
              <Select label="Entity type" value={form.entity_type} onChange={(e) => update("entity_type", e.target.value)} data-testid="onboarding-entity-type">
                {ENTITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </Select>
              <Input label="Legal organization name" value={form.legal_org_name} onChange={(e) => update("legal_org_name", e.target.value)} placeholder="Smith for Senate Committee" data-testid="onboarding-legal-name" />
              <Input label="Public campaign name" value={form.public_campaign_name} onChange={(e) => update("public_campaign_name", e.target.value)} placeholder="Smith for Senate" />
              {["candidate", "candidate_committee"].includes(form.entity_type) && (
                <>
                  <Input label="Candidate name" value={form.candidate_name} onChange={(e) => update("candidate_name", e.target.value)} />
                  <Input label="Office sought" value={form.office_sought} onChange={(e) => update("office_sought", e.target.value)} placeholder="US Senate, TX" />
                </>
              )}
            </div>
          )}

          {step === 1 && (
            <div className="grid grid-cols-2 gap-4" data-testid="onboarding-step-1">
              <Input label="Election" value={form.election} onChange={(e) => update("election", e.target.value)} placeholder="2026 General" />
              <Input label="Election date" type="date" value={form.election_date} onChange={(e) => update("election_date", e.target.value)} />
              <Input label="Jurisdiction" value={form.jurisdiction} onChange={(e) => update("jurisdiction", e.target.value)} placeholder="Federal / State / Local" />
              <Input label="State" value={form.state} onChange={(e) => update("state", e.target.value)} placeholder="TX" />
              <Input label="District" value={form.district} onChange={(e) => update("district", e.target.value)} placeholder="TX-32 or SD-12" />
              <Input label="Party (if applicable)" value={form.party} onChange={(e) => update("party", e.target.value)} />
              <Input label="Committee name" value={form.committee_name} onChange={(e) => update("committee_name", e.target.value)} className="col-span-2" />
              <Input label="Monthly budget (est.)" type="number" value={form.monthly_budget} onChange={(e) => update("monthly_budget", e.target.value)} />
              <Input label="Website" value={form.website} onChange={(e) => update("website", e.target.value)} />
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="onboarding-step-2">
              <Select label="Authorization status" value={form.authorization_status} onChange={(e) => update("authorization_status", e.target.value)}>
                {AUTH_OPTS.map(o => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
              </Select>
              <div className="grid grid-cols-2 gap-4">
                <Input label="FEC ID (if applicable)" value={form.fec_id} onChange={(e) => update("fec_id", e.target.value)} />
                <Input label="State registration ID" value={form.state_registration_id} onChange={(e) => update("state_registration_id", e.target.value)} />
                <Input label="Treasurer" value={form.treasurer} onChange={(e) => update("treasurer", e.target.value)} />
                <Input label="Compliance contact" value={form.compliance_contact} onChange={(e) => update("compliance_contact", e.target.value)} />
                <Input label="Legal counsel" value={form.legal_counsel} onChange={(e) => update("legal_counsel", e.target.value)} className="col-span-2" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1.5">Approved disclaimers (Paid for by…)</label>
                {form.approved_disclaimers.map((d, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <input
                      value={d}
                      onChange={(e) => {
                        const arr = [...form.approved_disclaimers]; arr[i] = e.target.value;
                        update("approved_disclaimers", arr);
                      }}
                      placeholder="Paid for by …"
                      className="flex-1 bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm"
                    />
                    {form.approved_disclaimers.length > 1 && (
                      <button onClick={() => update("approved_disclaimers", form.approved_disclaimers.filter((_, j) => j !== i))} className="text-slate-500 hover:text-rose-300 text-xs px-2">Remove</button>
                    )}
                  </div>
                ))}
                <Btn variant="secondary" onClick={() => update("approved_disclaimers", [...form.approved_disclaimers, ""])}>+ Add disclaimer</Btn>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="onboarding-step-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1.5">Brand primary color</label>
                  <div className="flex gap-2">
                    <input type="color" value={form.brand_primary_color} onChange={(e) => update("brand_primary_color", e.target.value)} className="w-14 h-10 rounded border border-white/10 bg-slate-900" />
                    <input value={form.brand_primary_color} onChange={(e) => update("brand_primary_color", e.target.value)} className="flex-1 bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm font-mono" />
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1.5">Brand secondary color</label>
                  <div className="flex gap-2">
                    <input type="color" value={form.brand_secondary_color} onChange={(e) => update("brand_secondary_color", e.target.value)} className="w-14 h-10 rounded border border-white/10 bg-slate-900" />
                    <input value={form.brand_secondary_color} onChange={(e) => update("brand_secondary_color", e.target.value)} className="flex-1 bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm font-mono" />
                  </div>
                </div>
              </div>
              <Textarea label="Approved messaging (top-line pitch)" value={form.approved_messaging} onChange={(e) => update("approved_messaging", e.target.value)} />
              <Textarea label="Notes (internal)" value={form.notes} onChange={(e) => update("notes", e.target.value)} />
            </div>
          )}

          {step === 4 && (
            <div data-testid="onboarding-step-4">
              <div className="rounded-md border border-cyan-400/30 bg-cyan-400/5 p-4 mb-4 flex gap-3">
                <ShieldCheck className="w-5 h-5 text-cyan-300 shrink-0" />
                <div>
                  <div className="text-cyan-300 font-semibold text-sm">This entity will be used for compliance workflows.</div>
                  <div className="text-xs text-slate-300 mt-1">All political campaigns will require disclaimers, entity verification, and human approval before launch.</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Rev k="Type" v={form.entity_type.replace(/_/g, " ")} />
                <Rev k="Legal name" v={form.legal_org_name || "—"} />
                <Rev k="Public name" v={form.public_campaign_name || "—"} />
                <Rev k="Office" v={form.office_sought || "—"} />
                <Rev k="Election" v={`${form.election} · ${form.election_date || "TBD"}`} />
                <Rev k="Jurisdiction" v={`${form.jurisdiction || "—"} · ${form.state || ""}`} />
                <Rev k="Authorization" v={form.authorization_status.replace(/_/g, " ")} />
                <Rev k="FEC ID" v={form.fec_id || "—"} />
                <Rev k="Treasurer" v={form.treasurer || "—"} />
                <Rev k="Monthly budget" v={form.monthly_budget ? `$${Number(form.monthly_budget).toLocaleString()}` : "—"} />
              </div>
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Disclaimer</div>
                <div className="text-sm mt-0.5 italic text-slate-300">{form.approved_disclaimers[0] || "None set"}</div>
              </div>
            </div>
          )}

          <div className="mt-6 flex justify-between">
            <Btn variant="secondary" onClick={back} disabled={step === 0}><ArrowLeft className="w-4 h-4" /> Back</Btn>
            {step < STEPS.length - 1 ? (
              <Btn onClick={next} data-testid="onboarding-next">Continue <ArrowRight className="w-4 h-4" /></Btn>
            ) : (
              <Btn onClick={submit} disabled={saving || !form.legal_org_name} data-testid="onboarding-submit">
                {saving ? "Saving…" : "Create entity & continue"}
              </Btn>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

const Rev = ({ k, v }) => (
  <div className="border-b border-white/5 py-2">
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{k}</div>
    <div className="text-white mt-0.5 truncate capitalize">{v}</div>
  </div>
);
